// Report language (owner's request, 2026-10-15): a saved report is written in English or French. Its AI findings are
// drafted in that language, can be translated into the other one (a stored translation keyed by language and by the
// findings revision it was made from), and its full report prints its fixed labels in the language chosen at export.
// A translation must keep every citation marker of the text it translates: `translationMarkerCheck` says whether it
// does, so a translation that drops, adds or rewrites a marker is refused rather than stored.

import { findingsBlocks } from './findings';

export const REPORT_LANGUAGES = ['en', 'fr'] as const;
export type ReportLanguage = (typeof REPORT_LANGUAGES)[number];
export const DEFAULT_REPORT_LANGUAGE: ReportLanguage = 'en';

export const isReportLanguage = (v: unknown): v is ReportLanguage => REPORT_LANGUAGES.includes(v as ReportLanguage);

/** The language's name in the interface (English), with its own name for French. */
export const REPORT_LANGUAGE_LABELS: Record<ReportLanguage, string> = { en: 'English', fr: 'French (français)' };
/** The language's name as a model instruction ("Write in French"). */
export const REPORT_LANGUAGE_NAMES: Record<ReportLanguage, string> = { en: 'English', fr: 'French' };

/** The other language (translations go from one to the other). */
export const otherLanguage = (l: ReportLanguage): ReportLanguage => (l === 'en' ? 'fr' : 'en');

/** A stored translation of findings: every section's title and text, with the revision it was made from. */
export interface FindingsTranslation {
  language: ReportLanguage;
  /** The language of the text it was translated from. */
  from: ReportLanguage;
  /** The findings revision translated; the translation is stale once the findings move on. */
  revision: number;
  sections: { key: string; title: string; body: string }[];
  /** Sections whose citation markers came back in another order within a paragraph (kept, but flagged to check). */
  reordered: string[];
  route: 'native' | 'anthropic' | 'openai' | null;
  model: string | null;
  translatedAt: number;
  translatedBy: string | null;
}

/** A translation as the findings view shows it. */
export interface FindingsTranslationView extends FindingsTranslation {
  /** Made from an older revision of the findings: "Translate again". */
  stale: boolean;
}

// ── Citation markers through a translation ───────────────────────────────────────────────────────────────────

const MARKER = /\[\s*(C[1-9]\d{0,3}(?:\s*[,;]\s*C[1-9]\d{0,3})*)\s*\]/g;

/** The markers of a text in order, each written the one way (`[C1, C4]`). */
export function markerList(text: string): string[] {
  return [...text.matchAll(MARKER)].map(m => `[${(m[1] as string).split(/\s*[,;]\s*/).join(', ')}]`);
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

export interface MarkerCheck {
  /** Every marker of the source is in the translation, as many times, and no other. */
  ok: boolean;
  /** Markers the translation lost (once per missing occurrence). */
  missing: string[];
  /** Markers the translation added or rewrote. */
  extra: string[];
  /** Same markers, but not in the same order paragraph by paragraph. */
  reordered: boolean;
}

/**
 * Checks one section's translation against its source: the same markers, as many times each (refuse otherwise), and,
 * ideally, in the same order within each paragraph (flagged when not, or when the paragraphs don't line up).
 */
export function translationMarkerCheck(source: string, translated: string): MarkerCheck {
  const a = markerList(source);
  const b = markerList(translated);
  const left = [...b];
  const missing: string[] = [];
  for (const m of a) {
    const i = left.indexOf(m);
    if (i >= 0) left.splice(i, 1);
    else missing.push(m);
  }
  const ok = !missing.length && !left.length;
  const blocksA = findingsBlocks(source).map(x => markerList(x.text));
  const blocksB = findingsBlocks(translated).map(x => markerList(x.text));
  const reordered = ok && (!sameList(a, b) || blocksA.length !== blocksB.length || blocksA.some((x, i) => !sameList(x, blocksB[i] ?? [])));
  return { ok, missing, extra: left, reordered };
}

/**
 * The findings' translations as the view shows them: those into another language than the text's own (a redraft in
 * the other language leaves one behind), each `stale` when made from an older revision.
 */
export function translationView(
  translations: readonly FindingsTranslation[] | null | undefined,
  revision: number,
  textLanguage: ReportLanguage,
): FindingsTranslationView[] {
  return (translations ?? []).filter(t => t.language !== textLanguage).map(t => ({ ...t, stale: t.revision !== revision }));
}
